import { useMemo, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, Sparkles } from "lucide-react";
import { Logo } from "../components/Logo";
import { NavMenu } from "../components/NavMenu";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import { formatApiError } from "../lib/api";

const GENDER_OPTIONS = [
  { value: "femme", label: "Une femme" },
  { value: "homme", label: "Un homme" },
];

const LOOKING_OPTIONS = [
  { value: "femmes", label: "Des femmes" },
  { value: "hommes", label: "Des hommes" },
  { value: "les_deux", label: "Les deux" },
];

const AGE_CHOICES = [18, 20, 25, 30, 35, 40, 45, 50, 55, 60, 65, 70, 75, 80];
const RADIUS_CHOICES = [[25, "25 km"], [50, "50 km"], [100, "100 km"], [200, "200 km"], [2000, "Toute la France"]];

const ChoiceRow = ({ options, value, onChange, testid }) => (
  <div className="flex gap-2" data-testid={testid}>
    {options.map((o) => (
      <button
        type="button"
        key={o.value}
        onClick={() => onChange(o.value)}
        data-testid={`${testid}-${o.value}`}
        className={`flex-1 rounded-xl border px-3 py-2.5 text-sm font-medium transition-all duration-300 ${
          value === o.value ? "border-terra bg-terra text-cream shadow-boho" : "border-terra/20 bg-cream text-clay hover:border-terra/60"
        }`}
      >
        {o.label}
      </button>
    ))}
  </div>
);

export default function Auth() {
  const [params] = useSearchParams();
  const [mode, setMode] = useState(params.get("mode") === "register" ? "register" : "login");
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [birthDate, setBirthDate] = useState("");
  const [city, setCity] = useState("");
  const [gender, setGender] = useState("");
  const [lookingFor, setLookingFor] = useState("");
  const [prefAgeMin, setPrefAgeMin] = useState(25);
  const [prefAgeMax, setPrefAgeMax] = useState(45);
  const [radius, setRadius] = useState(50);
  const [promo, setPromo] = useState("");
  const [acceptCgv, setAcceptCgv] = useState(false);
  const [acceptOrientation, setAcceptOrientation] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const { login, register } = useAuth();
  const navigate = useNavigate();

  const age = useMemo(() => {
    if (!birthDate) return null;
    const born = new Date(birthDate + "T00:00:00");
    if (isNaN(born.getTime())) return null;
    const t = new Date();
    let a = t.getFullYear() - born.getFullYear();
    if (t.getMonth() < born.getMonth() || (t.getMonth() === born.getMonth() && t.getDate() < born.getDate())) a -= 1;
    return a;
  }, [birthDate]);
  const maxBirthDate = useMemo(() => {
    const d = new Date();
    d.setFullYear(d.getFullYear() - 18);
    return d.toISOString().slice(0, 10);
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (mode === "register") {
      if (!birthDate || age === null || age > 120) {
        setError("Indiquez votre date de naissance.");
        return;
      }
      if (age < 18) {
        setError("APINITY est strictement réservé aux personnes majeures (18 ans et plus).");
        return;
      }
      if (!city.trim()) {
        setError("Indiquez votre ville — elle aide à proposer des profils proches de vous.");
        return;
      }
      if (!gender) {
        setError("Indiquez si vous êtes une femme ou un homme.");
        return;
      }
      if (!lookingFor) {
        setError("Indiquez qui vous souhaitez rencontrer.");
        return;
      }
    }
    setLoading(true);
    try {
      const data =
        mode === "register"
          ? await register({ name, email, password, birth_date: birthDate, city: city.trim(), gender, looking_for: lookingFor, preferred_age_min: Math.min(prefAgeMin, prefAgeMax), preferred_age_max: Math.max(prefAgeMin, prefAgeMax), search_radius_km: radius, promo_code: promo || undefined, accepte_cgv: acceptCgv, consentement_orientation: acceptOrientation })
          : await login(email, password);
      if (data.promo_applied) toast.success("Code d'invitation accepté — votre test est offert !");
      navigate(data.archetype ? "/discover" : "/quiz");
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  const inputCls = "w-full rounded-xl border border-terra/20 bg-cream px-4 py-3 text-sm text-ink outline-none transition-colors focus:border-terra";
  const labelCls = "mb-1.5 block text-xs font-semibold uppercase tracking-[0.2em] text-terra";

  return (
    <div className="grain relative flex min-h-screen items-center justify-center overflow-hidden bg-cream px-4 py-16" data-testid="auth-page">
      <img src="/assets/couple-hands.jpeg" alt="" className="absolute inset-0 h-full w-full object-cover opacity-[0.14]" />
      <div className="absolute inset-0 bg-gradient-to-b from-cream/70 via-cream/50 to-cream" />
      <div className="absolute left-4 right-4 top-5 z-10 flex items-center justify-between sm:left-8 sm:right-8">
        <Link to="/" data-testid="auth-header-logo"><Logo size={38} /></Link>
        <NavMenu />
      </div>
      <motion.div
        initial={{ opacity: 0, y: 32 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
        className="relative w-full max-w-md rounded-3xl border border-terra/15 bg-white/90 p-8 shadow-card backdrop-blur-md sm:p-10"
      >
        <Link to="/" className="mb-8 flex justify-center" data-testid="auth-logo-link">
          <Logo size={52} withWordmark={false} />
        </Link>
        <h1 className="text-center font-serif text-3xl font-medium tracking-tight text-ink" data-testid="auth-title">
          {mode === "register" ? "Créez votre compte" : "Ravi de vous revoir"}
        </h1>
        <p className="mt-2 text-center text-sm text-clay">
          {mode === "register" ? "Votre test de compatibilité en 10 questions vous attend." : "Votre profil vous attend."}
        </p>

        <form onSubmit={submit} className="mt-8 space-y-4">
          {mode === "register" && (
            <div>
              <label className={labelCls}>Prénom</label>
              <input
                data-testid="auth-name-input"
                value={name}
                onChange={(e) => setName(e.target.value)}
                required
                placeholder="Comment vous appelle-t-on ?"
                className={inputCls}
              />
            </div>
          )}
          <div>
            <label className={labelCls}>Email</label>
            <input
              data-testid="auth-email-input"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              placeholder="vous@exemple.fr"
              className={inputCls}
            />
          </div>
          <div>
            <label className={labelCls}>Mot de passe</label>
            <input
              data-testid="auth-password-input"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              minLength={6}
              placeholder="6 caractères minimum"
              className={inputCls}
            />
          </div>
          {mode === "register" && (
            <>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className={labelCls}>Date de naissance</label>
                  <input
                    data-testid="auth-birthdate-input"
                    type="date"
                    max={maxBirthDate}
                    value={birthDate}
                    onChange={(e) => setBirthDate(e.target.value)}
                    required
                    className={inputCls}
                  />
                </div>
                <div>
                  <label className={labelCls}>Ville</label>
                  <input
                    data-testid="auth-city-input"
                    value={city}
                    onChange={(e) => setCity(e.target.value)}
                    required
                    placeholder="Où vivez-vous ?"
                    className={inputCls}
                  />
                </div>
              </div>
              {age !== null && age >= 0 && age <= 120 && (
                <p className={`rounded-xl px-4 py-2.5 text-xs ${age >= 18 ? "bg-terra/10 text-clay" : "bg-red-50 font-semibold text-red-700"}`} data-testid="auth-age-computed">
                  {age >= 18
                    ? <>Vous avez <strong className="text-ink">{age} ans</strong> — âge calculé automatiquement (déclaré, non vérifié par pièce d'identité).</>
                    : <>Vous avez {age} ans — APINITY est strictement réservé aux personnes majeures (18 ans et plus).</>}
                </p>
              )}
              <div>
                <label className={labelCls}>Je suis…</label>
                <ChoiceRow options={GENDER_OPTIONS} value={gender} onChange={setGender} testid="auth-gender" />
              </div>
              <div>
                <label className={labelCls}>Je souhaite rencontrer…</label>
                <ChoiceRow options={LOOKING_OPTIONS} value={lookingFor} onChange={setLookingFor} testid="auth-looking" />
                <label className="mt-2 flex cursor-pointer items-start gap-2" data-testid="auth-orientation-label">
                  <input
                    type="checkbox"
                    checked={acceptOrientation}
                    onChange={(e) => setAcceptOrientation(e.target.checked)}
                    data-testid="auth-orientation-checkbox"
                    className="mt-0.5 h-3.5 w-3.5 shrink-0 accent-[#C86D51]"
                  />
                  <span className="text-[11px] leading-snug text-stone2">
                    J'accepte l'utilisation de ma préférence de rencontre pour recevoir des profils compatibles.{" "}
                    <Link to="/confidentialite" target="_blank" className="underline hover:text-terra" data-testid="auth-orientation-more-link">En savoir plus</Link>
                  </span>
                </label>
              </div>
              <div>
                <label className={labelCls}>Tranche d'âge souhaitée</label>
                <div className="flex items-center gap-2" data-testid="auth-pref-age">
                  <select value={prefAgeMin} onChange={(e) => setPrefAgeMin(Number(e.target.value))} className={inputCls} data-testid="auth-pref-age-min">
                    {AGE_CHOICES.map((a) => <option key={a} value={a}>{a} ans</option>)}
                  </select>
                  <span className="shrink-0 text-sm text-stone2">à</span>
                  <select value={prefAgeMax} onChange={(e) => setPrefAgeMax(Number(e.target.value))} className={inputCls} data-testid="auth-pref-age-max">
                    {AGE_CHOICES.map((a) => <option key={a} value={a}>{a} ans</option>)}
                  </select>
                </div>
              </div>
              <div>
                <label className={labelCls}>Distance maximum</label>
                <div className="flex flex-wrap gap-2" data-testid="auth-radius">
                  {RADIUS_CHOICES.map(([v, l]) => (
                    <button
                      type="button"
                      key={v}
                      onClick={() => setRadius(v)}
                      data-testid={`auth-radius-${v}`}
                      className={`rounded-full border px-3.5 py-2 text-xs font-medium transition-all duration-300 ${
                        radius === v ? "border-terra bg-terra text-cream shadow-boho" : "border-terra/20 bg-cream text-clay hover:border-terra/60"
                      }`}
                    >
                      {l}
                    </button>
                  ))}
                </div>
              </div>
              <div>
                <label className={labelCls}>
                  Code d'invitation <span className="font-normal normal-case tracking-normal text-stone2">(facultatif)</span>
                </label>
                <input
                  data-testid="auth-promo-input"
                  value={promo}
                  onChange={(e) => setPromo(e.target.value)}
                  placeholder="Un proche vous offre le test ?"
                  className={`${inputCls} uppercase`}
                />
              </div>
            </>
          )}
          {mode === "login" && (
            <div className="text-right">
              <Link to="/mot-de-passe-oublie" data-testid="auth-forgot-link" className="text-xs text-clay transition-colors hover:text-terra">
                Mot de passe oublié ?
              </Link>
            </div>
          )}
          {error && (
            <p className="rounded-xl bg-rosedust/20 px-4 py-3 text-sm text-terra-dark" data-testid="auth-error-message">{error}</p>
          )}
          {mode === "register" && (
            <label className="flex cursor-pointer items-start gap-3 rounded-xl border border-terra/20 bg-cream/60 px-4 py-3" data-testid="auth-cgv-label">
              <input
                type="checkbox"
                checked={acceptCgv}
                onChange={(e) => setAcceptCgv(e.target.checked)}
                data-testid="auth-cgv-checkbox"
                className="mt-0.5 h-4 w-4 shrink-0 accent-[#C86D51]"
              />
              <span className="text-xs leading-relaxed text-clay">
                J'ai lu et j'accepte les{" "}
                <Link to="/cgv" target="_blank" className="font-semibold text-terra underline" data-testid="auth-cgv-link">Conditions Générales de Vente</Link>, la{" "}
                <Link to="/pua" target="_blank" className="font-semibold text-terra underline" data-testid="auth-pua-link">Politique d'utilisation acceptable</Link> et la{" "}
                <Link to="/confidentialite" target="_blank" className="font-semibold text-terra underline" data-testid="auth-privacy-link">politique de confidentialité</Link>.
              </span>
            </label>
          )}
          <button
            type="submit"
            disabled={loading || (mode === "register" && (!acceptCgv || !acceptOrientation))}
            data-testid="auth-submit-button"
            className="group flex w-full items-center justify-center gap-2 rounded-full bg-terra px-6 py-3.5 text-sm font-semibold text-cream shadow-boho transition-all duration-300 hover:bg-terra-dark disabled:opacity-60"
          >
            {loading ? "Un instant…" : mode === "register" ? "Créer mon compte" : "Me connecter"}
            {!loading && <ArrowRight size={16} className="transition-transform duration-300 group-hover:translate-x-1" />}
          </button>
        </form>

        <button
          onClick={() => { setMode(mode === "register" ? "login" : "register"); setError(""); }}
          data-testid="auth-switch-mode-button"
          className="mt-6 flex w-full items-center justify-center gap-2 text-sm text-clay transition-colors hover:text-terra"
        >
          <Sparkles size={14} />
          {mode === "register" ? "Déjà inscrit·e ? Connectez-vous" : "Pas encore de compte ? Inscrivez-vous"}
        </button>
      </motion.div>
    </div>
  );
}
